import {
   all,
   call,
   delay,
   put,
   race,
   select,
   take,
   takeEvery,
   takeLatest,
   SagaReturnType,
} from 'redux-saga/effects';
import types from '../types';
import ApiService from 'api/ApiService';
import api from 'api';
import {DialogTypes} from 'common/names';
import {action, Action} from 'redux/helpers';
import actions from 'redux/keys/actions';
import {pushErrorToast} from 'features/notifications/redux/actions';
import selectors from 'features/layout/redux/selectors';
import {releaseSaga, suspendSaga} from 'redux/common/saga/releaseSuspend';
import history from 'redux/history';
import {CreateSSHKeyPair} from 'types/diplomat/keys/CreateSSHKeyPair';
import {packId} from 'common/diplomatIdUtil';
import {exportSaga} from './export';
import {importPgpKeySaga} from './importPgpKey';
import {isDialogActive} from 'redux/common/saga/isDialogActiveSaga';
import {closeDialog} from 'redux/common/saga/closeDialogSaga';
import {unhandedSagaError} from 'redux/common';
import {IDiplomatObject} from 'types/DiplomatObject';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {createOpenPGPKeyPairSaga} from 'redux/keys/sagas/createOpenPgpKeyPairSaga';
import {replaceKey} from './replaceKey';
import {importRemoteKey} from './importRemoteKey';
import {uploadRemoteKey} from './uploadRemoteKey';

function* createSSHKeyPairSaga({
   payload,
   callbacks,
}: Action<CreateSSHKeyPair, FormOnSubmitHelpers<any>>) {
   try {
      yield call(ApiService.postSecureJson, api.sshKeyPair, JSON.stringify(payload));
      yield put(actions.startPollSSHKeyPairStatus(payload!.keyName, callbacks!));
   } catch (e) {
      const {errors = []} = yield (e as Response).json();
      yield put(pushErrorToast(errors.map((e: any) => e.message)));
      yield put(action(types.CREATE_SSH_KEY_PAIR.ERROR, errors));
      const isActive = (yield select(
         selectors.isDialogActive,
         DialogTypes.CreateSSHKeyPair.id
      )) as ReturnType<typeof selectors.isDialogActive>;
      if (isActive && callbacks) {
         yield callbacks.setSubmitting(false);
      }
   }
}

function* pollSSHKeyPairStatusWorker(
   keyName: string,
   helpers: FormOnSubmitHelpers<IDiplomatObject>
) {
   try {
      let pollCount = 0;
      while (true) {
         pollCount++;
         const isActive = (yield call(
            isDialogActive,
            DialogTypes.CreateSSHKeyPair.id
         )) as SagaReturnType<typeof isDialogActive>;
         if (isActive) {
            helpers.setStatus({pollCount});
         }
         const response = (yield call(
            ApiService.getSecureJson,
            api.sshKeyPair + `?keyName=${keyName}`
         )) as Response;
         if (response.status === 201) {
            const values = (yield response.json()) as IDiplomatObject;
            yield call(closeDialog, DialogTypes.CreateSSHKeyPair.id);
            yield put(action(types.CREATE_SSH_KEY_PAIR.SUCCESS));
            pollCount = 0;
            history.push(`/${packId(values.objectType, values.id)}`);
            yield put(actions.stopPollSSHKeyPairStatus());
         }
         yield delay(1000);
      }
   } catch (e) {
      try {
         const {errors = []} = yield (e as Response).json();
         yield put(action(types.CREATE_SSH_KEY_PAIR.ERROR, errors));
         yield put(pushErrorToast(errors.map((e: any) => e.message)));
         const isActive = (yield call(
            isDialogActive,
            DialogTypes.CreateSSHKeyPair.id
         )) as SagaReturnType<typeof isDialogActive>;
         if (isActive) {
            yield helpers.setSubmitting(false);
         }
         yield put(actions.stopPollSSHKeyPairStatus());
      } catch (e) {
         yield call(unhandedSagaError, e as Error);
      }
   }
}

function* pollSSHKeyPairStatusWatcher({
   payload,
   callbacks,
}: Action<string, FormOnSubmitHelpers<any>>) {
   yield race([
      call(pollSSHKeyPairStatusWorker, payload!, callbacks!),
      take(types.STOP_POLL_SSH_KEY_PAIR_STATUS),
   ]);
}

export function* keysSaga() {
   yield all([
      takeLatest(types.RELEASE_KEY, releaseSaga),
      takeLatest(types.SUSPEND_KEY, suspendSaga),
      takeEvery(types.CREATE_SSH_KEY_PAIR.REQUEST, createSSHKeyPairSaga),
      takeEvery(types.START_POLL_SSH_KEY_PAIR_STATUS, pollSSHKeyPairStatusWatcher),
      takeEvery(types.CREATE_OPEN_PGP_KEY_PAIR.REQUEST, createOpenPGPKeyPairSaga),
      takeEvery(types.EXPORT.SAGA, exportSaga),
      takeLatest(types.IMPORT_PGP_PUBLIC.SAGA, importPgpKeySaga),
      takeLatest(types.IMPORT_PGP_KEY_PAIR.SAGA, importPgpKeySaga),
      takeLatest(types.REPLACE_KEY.SAGA, replaceKey),
      takeLatest(types.IMPORT_REMOTE_KEY.SAGA, importRemoteKey),
      takeLatest(types.UPLOAD_REMOTE_KEY.SAGA, uploadRemoteKey),
   ]);
}
